/**
 * ServiceForm.tsx
 *
 * Service create / edit form — mirrors mobile add_service_page.dart.
 *
 * Fields (in mobile order):
 *  1. Header banner card
 *  2. Title        (required)
 *  3. Description  (required)
 *  4. Price        (optional — "Add base price or leave blank")
 *  5. User Types   → opens UserTypeSelector modal
 *  6. Tags         → opens TagSelector modal
 *  7. Images       (optional, up to 5) — Cloudinary upload handled by parent
 *  8. Work sample links (optional, dynamic list with add/edit/remove)
 *
 * Validation mirrors mobile:
 *  - title    empty → toast error
 *  - description optional
 *  - price    optional
 *
 * The form calls onSubmit with ServiceFormValues; the parent route handles
 * Cloudinary upload and API mutation (same pattern as MediaForm + routes).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiX,
  FiPlus,
  FiLink,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiCheck,
  FiSearch,
} from 'react-icons/fi';
import { MdCloudUpload } from 'react-icons/md';
import { toast } from '../../utils/toast';
import { Button } from '../ui/Button';
import { FormLabel, OptionalBadge } from '../ui/FormLabel';
import { UserTypeSelector } from './UserTypeSelector';
import { DeleteServiceModal } from './DeleteServiceModal';
import { useGetTags } from '../../api/services';
import type { UserTypeItem, TagItem } from '../../api/services';
import { formatTagLabel } from '../../utils/tagLabel';
import { normalizeServicePriceValue, sanitizeServicePriceInput } from '../../utils/price';
import {
  UPLOAD_MAX_FILE_SIZE_BYTES,
  UPLOAD_MAX_FILE_SIZE_MB,
  UPLOAD_MAX_MEDIA_FILES,
  getUploadSizeLimitMessage,
  getUploadSizeLimitSkipMessage,
} from '../../utils/uploadValidation';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A single image slot in the ordered image list */
export type ImageEntry =
  | { type: 'existing'; url: string; id: string }
  | { type: 'new'; file: File; preview: string; id: string };

export interface ServiceFormValues {
  title: string;
  description: string;
  price: string;
  /** New local File objects to be uploaded (derived from orderedImageEntries) */
  files: File[];
  /** Existing remote Cloudinary URLs kept from edit mode (derived from orderedImageEntries) */
  existingImageUrls: string[];
  /** Ordered list of all images — use this to preserve user-defined drag order */
  orderedImageEntries: ImageEntry[];
  userTypeIds: number[];
  tagIds: number[];
  workExampleLinks: string[];
  alternateEmail: string;
}

export interface ServiceFormInitialData {
  title?: string;
  description?: string;
  price?: string;
  existingImageUrls?: string[];
  userTypeIds?: number[];
  tagIds?: number[];
  workExampleLinks?: string[];
  /** Pre-resolved display names for user types already selected */
  userTypeItems?: UserTypeItem[];
  /** Pre-resolved display names for tags already selected */
  tagItems?: TagItem[];
  alternateEmail?: string;
}

export interface ServiceFormProps {
  title: string;
  submitLabel: string;
  onSubmit: (values: ServiceFormValues) => void | Promise<void>;
  onDelete?: () => void;
  showDelete?: boolean;
  isLoading?: boolean;
  initialData?: ServiceFormInitialData;
  /** Server-side field errors from API validation (e.g., { description: "This field may not be blank." }) */
  serverErrors?: Record<string, string>;
  /** Callback to report when the form has unsaved changes */
  onDirtyChange?: (isDirty: boolean) => void;
}

const MAX_IMAGES = UPLOAD_MAX_MEDIA_FILES;
const MAX_FILE_SIZE_MB = UPLOAD_MAX_FILE_SIZE_MB;
const MAX_FILE_SIZE_BYTES = UPLOAD_MAX_FILE_SIZE_BYTES;
const PRICE_INPUT_HINT = 'Enter numbers only (currency is already applied)';
const INVALID_PRICE_FORMAT = 'Invalid price format';
const ALLOWED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/webm',
]);

// ─── Add-link modal ───────────────────────────────────────────────────────────

interface AddLinkModalProps {
  initialValue?: string;
  onConfirm: (value: string) => void;
  onClose: () => void;
}

const AddLinkModal: React.FC<AddLinkModalProps> = ({ initialValue = '', onConfirm, onClose }) => {
  const [value, setValue] = useState(initialValue);

  const formatLink = (raw: string) => {
    const s = raw.trim();
    if (!s) return s;
    if (!s.startsWith('http://') && !s.startsWith('https://')) return `https://${s}`;
    return s;
  };

  return (
    <div className="fixed inset-0 z-120 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        className="w-full sm:max-w-md bg-[#0c0c0c] border border-white/10 rounded-3xl overflow-hidden shadow-2xl"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
          <h3 className="text-white text-base font-semibold">
            {initialValue ? 'Edit Link' : 'Add Link'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-zinc-500 hover:text-white transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>
        <div className="p-6 flex flex-col gap-4">
          <input
            type="url"
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="https://example.com"
            className="w-full bg-white/6 border border-white/15 rounded-2xl px-4 h-12 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all"
          />
          <button
            type="button"
            onClick={() => {
              if (value.trim()) {
                onConfirm(formatLink(value));
              }
              onClose();
            }}
            className="w-full h-12 rounded-2xl bg-white text-black text-sm font-semibold hover:bg-zinc-200 transition-colors"
          >
            {initialValue ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.97); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

interface ServiceFormErrors {
  title?: string;
  description?: string;
  price?: string;
  tagIds?: string;
  alternateEmail?: string;
  workExampleLinks?: string;
}

export const ServiceForm: React.FC<ServiceFormProps> = ({
  title: pageTitle,
  submitLabel,
  onSubmit,
  onDelete,
  showDelete = false,
  isLoading = false,
  initialData,
  serverErrors = {},
  onDirtyChange,
}) => {
  // ── field state ──
  const [titleValue, setTitleValue] = useState(initialData?.title ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [price, setPrice] = useState(normalizeServicePriceValue(initialData?.price ?? ''));
  const [alternateEmail, setAlternateEmail] = useState(initialData?.alternateEmail ?? '');

  // ── tag search ──
  const [tagSearch, setTagSearch] = useState('');
  const { data: allTags = [] } = useGetTags('');

  // ── error state ──
  const [localErrors, setLocalErrors] = useState<ServiceFormErrors>({});

  // Merge local validation errors with server-side errors
  const errors = useMemo<ServiceFormErrors>(() => {
    return { ...localErrors, ...serverErrors };
  }, [localErrors, serverErrors]);

  // Auto-scroll to first error field when server errors change
  useEffect(() => {
    if (Object.keys(serverErrors).length === 0) return;

    // Small delay to allow DOM to update with error states
    const timer = setTimeout(() => {
      const firstErrorField = Object.keys(serverErrors)[0];
      const errorElement = document.querySelector(`[data-field="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Add a brief highlight animation
        errorElement.classList.add('ring-2', 'ring-red-500', 'ring-offset-2', 'ring-offset-black');
        setTimeout(() => {
          errorElement.classList.remove(
            'ring-2',
            'ring-red-500',
            'ring-offset-2',
            'ring-offset-black',
          );
        }, 1500);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [serverErrors]);

  // ── image state — unified ordered list ──
  const [imageList, setImageList] = useState<ImageEntry[]>(
    (initialData?.existingImageUrls ?? []).map((url) => ({
      type: 'existing' as const,
      url,
      id: crypto.randomUUID(),
    })),
  );
  const photoInputRef = useRef<HTMLInputElement>(null);
  const formTopRef = useRef<HTMLDivElement>(null);
  const dragIndexRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);

  // ── lightbox preview state ──
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (previewIndex === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreviewIndex(null);
      if (e.key === 'ArrowRight')
        setPreviewIndex((p) => (p !== null && p < imageList.length - 1 ? p + 1 : p));
      if (e.key === 'ArrowLeft') setPreviewIndex((p) => (p !== null && p > 0 ? p - 1 : p));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [previewIndex, imageList.length]);

  // ── selection state ──
  const [selectedUserTypeIds, setSelectedUserTypeIds] = useState<number[]>(
    initialData?.userTypeIds ?? [],
  );
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(initialData?.tagIds ?? []);

  // ── work sample links ──
  const [links, setLinks] = useState<string[]>(
    initialData?.workExampleLinks?.length ? initialData.workExampleLinks : [],
  );

  // ── dirty state tracking ──
  useEffect(() => {
    if (!onDirtyChange) return;

    const initialTitle = initialData?.title ?? '';
    const initialDescription = initialData?.description ?? '';
    const initialPrice = normalizeServicePriceValue(initialData?.price ?? '');
    const initialAlternateEmail = initialData?.alternateEmail ?? '';
    const initialImageUrls = initialData?.existingImageUrls ?? [];
    const initialUserTypeIds = initialData?.userTypeIds ?? [];
    const initialTagIds = initialData?.tagIds ?? [];
    const initialLinks = initialData?.workExampleLinks ?? [];

    // Compare image lists (existing URLs only, in order)
    const currentImageUrls = imageList
      .filter((e): e is { type: 'existing'; url: string; id: string } => e.type === 'existing')
      .map((e) => e.url);
    const imagesChanged =
      currentImageUrls.length !== initialImageUrls.length ||
      currentImageUrls.some((url, i) => url !== initialImageUrls[i]);

    // Compare user type IDs (sorted for order-independent comparison)
    const userTypesChanged =
      selectedUserTypeIds.length !== initialUserTypeIds.length ||
      selectedUserTypeIds.sort().some((id, i) => id !== initialUserTypeIds.slice().sort()[i]);

    // Compare tag IDs (sorted for order-independent comparison)
    const tagsChanged =
      selectedTagIds.length !== initialTagIds.length ||
      selectedTagIds.sort().some((id, i) => id !== initialTagIds.slice().sort()[i]);

    // Compare links (in order)
    const linksChanged =
      links.length !== initialLinks.length || links.some((link, i) => link !== initialLinks[i]);

    const isDirty =
      titleValue !== initialTitle ||
      description !== initialDescription ||
      price !== initialPrice ||
      alternateEmail !== initialAlternateEmail ||
      imagesChanged ||
      userTypesChanged ||
      tagsChanged ||
      linksChanged ||
      // Check if there are new images that need to be uploaded
      imageList.some((e) => e.type === 'new');

    onDirtyChange(isDirty);
  }, [
    titleValue,
    description,
    price,
    alternateEmail,
    imageList,
    selectedUserTypeIds,
    selectedTagIds,
    links,
    initialData,
    onDirtyChange,
  ]);

  // ── modal state ──
  const [showUserTypeSelector, setShowUserTypeSelector] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [linkModalState, setLinkModalState] = useState<{
    open: boolean;
    editIndex: number | null;
  }>({ open: false, editIndex: null });

  const totalImages = imageList.length;
  const canAddMoreImages = totalImages < MAX_IMAGES;

  // ── image handling ────────────────────────────────────────────────────────

  const addImages = useCallback(
    (incoming: FileList | null) => {
      if (!incoming) return;

      const all = Array.from(incoming);

      // ── Validate each file ──────────────────────────────────────────
      const invalidType: string[] = [];
      const tooLarge: string[] = [];
      const valid: File[] = [];

      for (const f of all) {
        if (!ALLOWED_MIME_TYPES.has(f.type)) {
          invalidType.push(f.name);
        } else if (f.size > MAX_FILE_SIZE_BYTES) {
          tooLarge.push(f.name);
        } else {
          valid.push(f);
        }
      }

      if (invalidType.length > 0) {
        toast.error(
          `Unsupported file${invalidType.length > 1 ? 's' : ''}: ${invalidType.join(', ')}. Only JPEG, PNG, WebP, GIF and MP4/MOV/WebM videos are allowed.`,
        );
      }
      if (tooLarge.length > 0) {
        toast.error(
          tooLarge.length === all.length
            ? getUploadSizeLimitMessage(MAX_FILE_SIZE_MB)
            : getUploadSizeLimitSkipMessage(tooLarge.length, MAX_FILE_SIZE_MB),
        );
      }

      if (valid.length === 0) return;

      const remaining = MAX_IMAGES - imageList.length;
      if (remaining <= 0) {
        toast.warn('You can add at most 5 media files.');
        return;
      }

      const arr = valid.slice(0, remaining);
      if (valid.length > remaining) {
        toast.info(`Only ${remaining} file(s) added — 5 media limit reached.`);
      }

      const newEntries: ImageEntry[] = arr.map((f) => ({
        type: 'new' as const,
        file: f,
        preview: URL.createObjectURL(f),
        id: crypto.randomUUID(),
      }));
      setImageList((prev) => [...prev, ...newEntries]);
    },
    [imageList.length],
  );

  const removeImage = (idx: number) => {
    setImageList((prev) => {
      const entry = prev[idx];
      if (entry.type === 'new') URL.revokeObjectURL(entry.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // ── drag-and-drop reordering ──────────────────────────────────────────────

  const handleDragStart = (idx: number) => {
    isDraggingRef.current = true;
    dragIndexRef.current = idx;
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === null || from === idx) return;
    setImageList((prev) => {
      const next = [...prev];
      const [dragged] = next.splice(from, 1);
      next.splice(idx, 0, dragged);
      dragIndexRef.current = idx;
      return next;
    });
  };

  const handleDragEnd = () => {
    // Delay so the click event that fires after dragend sees the flag
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 50);
    dragIndexRef.current = null;
  };

  const handlePriceChange = (rawValue: string) => {
    const trimmed = rawValue.trim();
    const sanitized = sanitizeServicePriceInput(trimmed);
    const containsInvalidChars = trimmed !== sanitized;

    setPrice(sanitized);
    setLocalErrors((prev) => ({
      ...prev,
      price: containsInvalidChars ? PRICE_INPUT_HINT : undefined,
    }));
  };

  const handlePricePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text');
    handlePriceChange(pasted);
  };

  // ── submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: ServiceFormErrors = {};
    let hasErrors = false;

    // Validate title
    if (!titleValue.trim()) {
      newErrors.title = 'Title is required.';
      hasErrors = true;
    }

    // Validate description
    // if (!description.trim()) {
    //   newErrors.description = 'Description is required.';
    //   hasErrors = true;
    // }

    // If there are errors, set state and show toast
    if (hasErrors) {
      setLocalErrors(newErrors);
      toast.error('Please complete all required fields.');
      // Scroll back to the top so the user sees the inline error messages
      formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Clear any previous local errors before submitting
    setLocalErrors({});

    // Price validation — numeric only with optional 2 decimal places
    const normalizedPrice = normalizeServicePriceValue(price);
    if (price.trim() && !normalizedPrice) {
      setLocalErrors((prev) => ({ ...prev, price: PRICE_INPUT_HINT }));
      toast.error(INVALID_PRICE_FORMAT);
      return;
    }

    if (normalizedPrice && !/^\d+(\.\d{1,2})?$/.test(normalizedPrice)) {
      setLocalErrors((prev) => ({ ...prev, price: INVALID_PRICE_FORMAT }));
      toast.error(INVALID_PRICE_FORMAT);
      return;
    }

    // URL validation on work sample links
    const invalidLinks = links.filter((l) => l.trim() && !/^https?:\/\/.+/.test(l.trim()));
    if (invalidLinks.length > 0) {
      toast.warn('One or more work sample links are not valid URLs. Please check and try again.');
      return;
    }

    await onSubmit({
      title: titleValue.trim(),
      description: description.trim(),
      price: normalizedPrice,
      files: imageList
        .filter(
          (e): e is { type: 'new'; file: File; preview: string; id: string } => e.type === 'new',
        )
        .map((e) => e.file),
      existingImageUrls: imageList
        .filter((e): e is { type: 'existing'; url: string; id: string } => e.type === 'existing')
        .map((e) => e.url),
      orderedImageEntries: imageList,
      userTypeIds: selectedUserTypeIds,
      tagIds: selectedTagIds,
      workExampleLinks: links.filter(Boolean),
      alternateEmail: alternateEmail.trim(),
    });
  };

  // ── link helpers ──────────────────────────────────────────────────────────

  const openAddLink = () => setLinkModalState({ open: true, editIndex: null });
  const openEditLink = (idx: number) => setLinkModalState({ open: true, editIndex: idx });
  const closeLink = () => setLinkModalState({ open: false, editIndex: null });

  const confirmLink = (value: string) => {
    if (linkModalState.editIndex !== null) {
      setLinks((prev) => prev.map((l, i) => (i === linkModalState.editIndex ? value : l)));
    } else {
      setLinks((prev) => [...prev, value]);
    }
  };

  const removeLink = (idx: number) => setLinks((prev) => prev.filter((_, i) => i !== idx));

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div ref={formTopRef} className="max-w-[1440px] mx-auto px-4 py-8">
      {/* Hidden file input */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/x-msvideo,video/webm"
        multiple
        className="hidden"
        onChange={(e) => addImages(e.target.files)}
        onClick={(e) => ((e.target as HTMLInputElement).value = '')}
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-10">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <h1 className="text-white text-2xl sm:text-[32px] font-bold">{pageTitle}</h1>
          <div className="hidden sm:flex items-center gap-4">
            <Button
              type="submit"
              size="lg"
              disabled={isLoading}
              className="rounded-full px-8 py-3 bg-white text-black hover:bg-zinc-200 focus:ring-0 disabled:opacity-50"
            >
              {isLoading ? 'Saving…' : submitLabel}
            </Button>
            {showDelete && onDelete && (
              <Button
                type="button"
                variant="secondary"
                size="lg"
                className="rounded-full px-8 py-3 bg-zinc-900 border border-white/10 text-red-500 hover:bg-red-500/10 focus:ring-0"
                onClick={() => setIsDeleteModalOpen(true)}
              >
                Delete
              </Button>
            )}
          </div>
        </div>

        {/* ── Two-column grid ──────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* ── Left Column ─────────────────────────────────────────── */}
          <div className="flex flex-col gap-8">
            {/* Title */}
            <div className="flex flex-col gap-3" data-field="title">
              <FormLabel required>Title</FormLabel>
              <input
                type="text"
                value={titleValue}
                onChange={(e) => {
                  setTitleValue(e.target.value);
                  if (localErrors.title) setLocalErrors((prev) => ({ ...prev, title: undefined }));
                }}
                placeholder="Enter Service title"
                className={`w-full h-14 bg-zinc-900/40 border rounded-2xl px-5 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all text-[15px] ${
                  errors.title ? 'border-red-500/60' : 'border-white/25'
                }`}
              />
              {errors.title && (
                <p className="text-red-400 text-[12px] animate-pulse">{errors.title}</p>
              )}
            </div>

            {/* Price */}
            <div className="flex flex-col gap-3" data-field="price">
              <FormLabel optional>Price</FormLabel>
              <p className="text-zinc-500 text-[12px] -mt-1">
                Add a base price or leave blank if you price by request
              </p>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex w-12 items-center justify-center rounded-l-2xl border-r border-white/15 bg-zinc-800/70 text-[15px] font-semibold text-white/90">
                  $
                </div>
                <input
                  type="text"
                  value={price}
                  onChange={(e) => handlePriceChange(e.target.value)}
                  onPaste={handlePricePaste}
                  inputMode="decimal"
                  placeholder="100"
                  className={`w-full h-14 bg-zinc-900/40 border rounded-2xl pl-16 pr-5 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all text-[15px] ${
                    errors.price ? 'border-red-500/60' : 'border-white/25'
                  }`}
                />
              </div>
              {errors.price && (
                <p className="text-red-400 text-[12px] animate-pulse">{errors.price}</p>
              )}
            </div>

            {/* Tag inline picker */}
            <div className="flex flex-col gap-4" data-field="tagIds">
              <FormLabel optional>Tag</FormLabel>
              {errors.tagIds && (
                <p className="text-red-400 text-[12px] animate-pulse -mb-2">{errors.tagIds}</p>
              )}

              {/* Search */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center bg-zinc-900/40 border border-white/25 rounded-2xl px-4 h-12 focus-within:border-blue-500/40 transition-all">
                  <FiSearch size={15} className="text-zinc-500 shrink-0" />
                  <input
                    type="text"
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    placeholder="Search..."
                    className="ml-3 bg-transparent border-none text-white text-[14px] placeholder:text-zinc-600 focus:ring-0 focus:outline-none w-full"
                  />
                  {tagSearch && (
                    <button
                      type="button"
                      onClick={() => setTagSearch('')}
                      className="text-zinc-500 hover:text-white transition-colors"
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </div>
                <p className="text-zinc-500 text-[12px] px-1">
                  Search and select a tag that fits the service rendered
                </p>
              </div>

              {/* Grouped pills */}
              {(() => {
                const filtered = allTags.filter((t) =>
                  t.name.toLowerCase().includes(tagSearch.toLowerCase()),
                );

                const selectedTags = allTags.filter((t) => selectedTagIds.includes(t.id));

                const groups: Record<string, TagItem[]> = {};
                for (const tag of filtered) {
                  const rawCategory = tag.category?.trim();
                  const cat =
                    !rawCategory || /^others?$/i.test(rawCategory) ? 'Tag List' : rawCategory;
                  (groups[cat] ??= []).push(tag);
                }

                const entries = Object.entries(groups);

                if (filtered.length === 0 && tagSearch) {
                  return (
                    <p className="text-zinc-500 text-[13px]">
                      No tags found for &ldquo;{tagSearch}&rdquo;
                    </p>
                  );
                }

                return (
                  <div className="flex flex-col gap-4">
                    {selectedTags.length > 0 && (
                      <div className="flex flex-col gap-3">
                        <p className="text-blue-400 text-[11px] font-semibold uppercase tracking-widest">
                          Selected Tags
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedTags.map((tag) => (
                            <button
                              key={`selected-${tag.id}`}
                              type="button"
                              onClick={() =>
                                setSelectedTagIds((prev) => prev.filter((id) => id !== tag.id))
                              }
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium transition-all border bg-zinc-800 border-white/20 text-white"
                            >
                              <FiCheck size={12} strokeWidth={3} />
                              {formatTagLabel(tag.name)}
                            </button>
                          ))}
                        </div>
                        <div className="w-full h-px bg-white/5 my-1" />
                      </div>
                    )}

                    <div className="h-64 overflow-y-auto pr-1 flex flex-col gap-5 [scrollbar-width:thin] [scrollbar-color:var(--color-zinc-700)_transparent]">
                      {entries.map(([category, tags]) => (
                        <div key={category} className="flex flex-col gap-3">
                          <p className="sticky top-0 z-20 mb-3 border-b border-white/10 pb-2 backdrop-blur-sm text-zinc-500 text-[11px] font-semibold uppercase tracking-widest">
                            {category}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {tags.map((tag) => {
                              const isSelected = selectedTagIds.includes(tag.id);
                              const normalizedTagName = /^others?$/i.test(tag.name.trim())
                                ? 'Tag List'
                                : formatTagLabel(tag.name);
                              return (
                                <button
                                  key={tag.id}
                                  type="button"
                                  onClick={() =>
                                    setSelectedTagIds((prev) =>
                                      isSelected
                                        ? prev.filter((id) => id !== tag.id)
                                        : [...prev, tag.id],
                                    )
                                  }
                                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium transition-all border ${
                                    isSelected
                                      ? 'bg-zinc-800 border-white/20 text-white'
                                      : 'bg-transparent border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-200'
                                  }`}
                                >
                                  {isSelected && <FiCheck size={12} strokeWidth={3} />}
                                  {normalizedTagName}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-zinc-500 text-[12px] text-center italic mt-1">
                      Scroll to view tag list
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* ── Right Column ────────────────────────────────────────── */}
          <div className="flex flex-col gap-8">
            {/* Optional header */}
            {/* <div className="flex items-center gap-4">
              <span className="text-zinc-400 text-[13px] font-medium shrink-0">Optional</span>
              <div className="h-px bg-white/8 flex-1" />
            </div> */}

            {/* Alternate Email */}
            <div className="flex flex-col gap-3" data-field="alternateEmail">
              <FormLabel optional>Alternate Email</FormLabel>
              <p className="text-zinc-500 text-[12px] -mt-1">
                Alternate email for receiving requests
              </p>
              <input
                type="email"
                value={alternateEmail}
                onChange={(e) => setAlternateEmail(e.target.value)}
                placeholder="e.g. johndoe@gmail.com"
                className={`w-full h-14 bg-zinc-900/40 border rounded-2xl px-5 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all text-[15px] ${
                  errors.alternateEmail ? 'border-red-500/60' : 'border-white/25'
                }`}
              />
              {errors.alternateEmail && (
                <p className="text-red-400 text-[12px] animate-pulse">{errors.alternateEmail}</p>
              )}
            </div>

            {/* Description */}
            <div className="flex flex-col gap-3" data-field="description">
              <FormLabel optional>Description</FormLabel>
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (localErrors.description)
                    setLocalErrors((prev) => ({ ...prev, description: undefined }));
                }}
                placeholder="Add description..."
                className={`w-full h-36 bg-zinc-900/40 border rounded-2xl px-5 py-4 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all resize-none text-[15px] ${
                  errors.description ? 'border-red-500/60' : 'border-white/25'
                }`}
              />
              {errors.description && (
                <p className="text-red-400 text-[12px] animate-pulse">{errors.description}</p>
              )}
            </div>

            {/* Work sample link */}
            <div className="flex flex-col gap-3" data-field="workExampleLinks">
              <FormLabel optional>Work sample link</FormLabel>
              {errors.workExampleLinks && (
                <p className="text-red-400 text-[12px] animate-pulse">{errors.workExampleLinks}</p>
              )}

              {links.length > 0 && (
                <div className="flex flex-col gap-2">
                  {links.map((link, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEditLink(i)}
                        className="flex-1 flex items-center gap-3 bg-zinc-900/40 border border-white/25 rounded-2xl px-4 h-12 text-left hover:bg-white/5 transition-all"
                      >
                        <FiLink size={14} className="text-blue-500 shrink-0" />
                        <span className="text-[14px] text-white truncate min-w-0">{link}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => removeLink(i)}
                        className="w-9 h-9 rounded-xl bg-red-500/10 flex items-center justify-center text-red-500 hover:bg-red-500/20 transition-colors shrink-0"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {links.length === 0 && (
                <div
                  onClick={openAddLink}
                  className="flex items-center gap-3 bg-zinc-900/40 border border-white/25 rounded-2xl px-4 h-12 cursor-pointer hover:bg-white/5 transition-all"
                >
                  <FiLink size={14} className="text-blue-500 shrink-0" />
                  <span className="text-zinc-600 text-[14px]">Enter work sample link</span>
                </div>
              )}

              <button
                type="button"
                onClick={openAddLink}
                className="flex items-center gap-1.5 text-blue-500 text-[13px] font-medium hover:text-blue-400 transition-colors w-fit"
              >
                <FiPlus size={14} strokeWidth={2.5} />
                Add more links
              </button>
            </div>

            {/* Upload area */}
            <div className="flex flex-col gap-4">
              <div
                onClick={() => canAddMoreImages && photoInputRef.current?.click()}
                className={`w-full rounded-2xl border bg-zinc-900/40 flex flex-col items-center justify-center gap-3 py-10 transition-all ${
                  canAddMoreImages
                    ? 'border-white/15 hover:bg-white/5 hover:border-white/25 cursor-pointer'
                    : 'border-white/25 opacity-60 cursor-not-allowed'
                }`}
              >
                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-zinc-400">
                  <MdCloudUpload size={26} />
                </div>
                <div className="text-center">
                  <div className="flex items-center gap-2">
                    <p className="text-zinc-400 text-[14px] font-medium">Add photo or videos</p>
                    <OptionalBadge />
                  </div>
                  <p className="text-zinc-600 text-[12px] mt-0.5">
                    JPEG · PNG · MP4 · MOV · WebM &nbsp;&bull;&nbsp; max {MAX_FILE_SIZE_MB} MB per
                    file &nbsp;&bull;&nbsp; up to {MAX_IMAGES} files
                  </p>
                </div>
              </div>

              {totalImages > 0 && (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <p className="text-zinc-500 text-[12px] font-medium uppercase tracking-wider">
                      Uploaded media
                    </p>
                    <span
                      className={`text-[12px] font-medium tabular-nums ${
                        totalImages >= MAX_IMAGES ? 'text-amber-400' : 'text-zinc-500'
                      }`}
                    >
                      {totalImages} / {MAX_IMAGES}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {imageList.map((entry, i) => {
                      const src = entry.type === 'existing' ? entry.url : entry.preview;
                      return (
                        <div
                          key={entry.id}
                          draggable
                          onDragStart={() => handleDragStart(i)}
                          onDragOver={(e) => handleDragOver(e, i)}
                          onDragEnd={handleDragEnd}
                          onClick={() => {
                            if (!isDraggingRef.current) setPreviewIndex(i);
                          }}
                          className="relative aspect-square rounded-xl overflow-hidden group/thumb cursor-grab active:cursor-grabbing select-none"
                        >
                          <img
                            src={src}
                            alt=""
                            className="w-full h-full object-cover pointer-events-none"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage(i);
                            }}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center text-white opacity-0 group-hover/thumb:opacity-100 transition-opacity"
                          >
                            <FiX size={11} />
                          </button>
                        </div>
                      );
                    })}
                    {canAddMoreImages && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          photoInputRef.current?.click();
                        }}
                        className="aspect-square rounded-xl border-2 border-dashed border-white/10 bg-zinc-900/40 flex items-center justify-center text-zinc-600 hover:text-white hover:border-white/20 transition-all cursor-pointer"
                      >
                        <FiPlus size={22} />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Mobile action buttons ────────────────────────────────────── */}
        <div className="flex sm:hidden flex-col gap-4 pt-8 border-t border-white/5">
          <Button
            type="submit"
            size="lg"
            disabled={isLoading}
            className="w-full rounded-full h-14 bg-white text-black hover:bg-zinc-200 focus:ring-0 disabled:opacity-50"
          >
            {isLoading ? 'Saving…' : submitLabel}
          </Button>
          {showDelete && onDelete && (
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full rounded-full h-14 bg-zinc-900 border border-white/10 text-red-500 hover:bg-red-500/10 focus:ring-0"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              Delete Service
            </Button>
          )}
        </div>
      </form>

      {/* ── Upload overlay ──────────────────────────────────────────────── */}
      {isLoading && (
        <div className="fixed inset-0 z-200 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-zinc-900 border border-white/10 rounded-4xl p-8 flex flex-col items-center gap-6 mx-4 w-full max-w-sm shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center">
              <MdCloudUpload size={32} className="text-blue-500" />
            </div>
            <div className="text-center">
              <p className="text-white font-semibold text-lg">
                {submitLabel === 'Update' ? 'Updating service…' : 'Creating service…'}
              </p>
              <p className="text-zinc-500 text-sm mt-1">Please wait…</p>
            </div>
            <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full animate-pulse w-full" />
            </div>
          </div>
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      {showUserTypeSelector && (
        <UserTypeSelector
          initialSelectedIds={selectedUserTypeIds}
          onConfirm={(ids) => {
            setSelectedUserTypeIds(ids);
            setShowUserTypeSelector(false);
          }}
          onClose={() => setShowUserTypeSelector(false)}
        />
      )}

      {linkModalState.open && (
        <AddLinkModal
          initialValue={linkModalState.editIndex !== null ? links[linkModalState.editIndex] : ''}
          onConfirm={confirmLink}
          onClose={closeLink}
        />
      )}

      {isDeleteModalOpen && onDelete && (
        <DeleteServiceModal
          onConfirm={() => {
            setIsDeleteModalOpen(false);
            onDelete();
          }}
          onCancel={() => setIsDeleteModalOpen(false)}
        />
      )}

      {/* ── Image lightbox ──────────────────────────────────────────────── */}
      {previewIndex !== null && (
        <div
          className="fixed inset-0 z-500 bg-black/95 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setPreviewIndex(null)}
        >
          <button
            type="button"
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              setPreviewIndex(null);
            }}
          >
            <FiX size={20} />
          </button>
          <div className="absolute top-5 left-1/2 -translate-x-1/2 text-zinc-400 text-sm font-medium select-none">
            {previewIndex + 1} / {imageList.length}
          </div>
          {previewIndex > 0 && (
            <button
              type="button"
              className="absolute left-4 sm:left-8 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
              onClick={(e) => {
                e.stopPropagation();
                setPreviewIndex((p) => (p !== null ? p - 1 : p));
              }}
            >
              <FiChevronLeft size={24} />
            </button>
          )}
          <img
            src={
              imageList[previewIndex].type === 'existing'
                ? imageList[previewIndex].url
                : (imageList[previewIndex] as { type: 'new'; preview: string }).preview
            }
            alt={`Preview ${previewIndex + 1}`}
            className="max-w-[88vw] max-h-[82vh] object-contain rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />
          {previewIndex < imageList.length - 1 && (
            <button
              type="button"
              className="absolute right-4 sm:right-8 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
              onClick={(e) => {
                e.stopPropagation();
                setPreviewIndex((p) => (p !== null ? p + 1 : p));
              }}
            >
              <FiChevronRight size={24} />
            </button>
          )}
          {imageList.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {imageList.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewIndex(idx);
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === previewIndex ? 'bg-white w-5' : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
